import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Toast, ToastConfig, ToastPosition, ToastAnimation, ToastOffset, ToastGlobalConfig } from '../models/toast.model';

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toasts$ = new BehaviorSubject<Toast[]>([]);
  private defaultPosition: ToastPosition = 'top-right';
  private defaultAnimation: ToastAnimation = 'fadeInSlide';
  private defaultDuration = 5000;
  private offset$ = new BehaviorSubject<ToastOffset>({});

  getOffset(): Observable<ToastOffset> {
    return this.offset$.asObservable();
  }

  getToasts(): Observable<Toast[]> {
    return this.toasts$.asObservable();
  }

  success(message: string, options?: Partial<ToastConfig>): void {
    this.show({
      message,
      type: 'success',
      ...options
    });
  }

  error(message: string, options?: Partial<ToastConfig>): void {
    this.show({
      message,
      type: 'error',
      ...options
    });
  }

  warning(message: string, options?: Partial<ToastConfig>): void {
    this.show({
      message,
      type: 'warning',
      ...options
    });
  }

  info(message: string, options?: Partial<ToastConfig>): void {
    this.show({
      message,
      type: 'info',
      ...options
    });
  }

  private show(config: ToastConfig): void {
    const toast: Toast = {
      id: this.generateId(),
      message: config.message,
      type: config.type,
      position: config.position || this.defaultPosition,
      animation: config.animation || this.defaultAnimation,
      duration: config.duration !== undefined ? config.duration : this.defaultDuration,
      actions: config.actions,
      visible: true
    };

    const currentToasts = this.toasts$.value;
    this.toasts$.next([...currentToasts, toast]);

    if (toast.duration > 0) {
      setTimeout(() => {
        this.dismiss(toast.id);
      }, toast.duration);
    }
  }

  dismiss(id: string): void {
    const currentToasts = this.toasts$.value;
    const toast = currentToasts.find(t => t.id === id);
    
    if (toast) {
      toast.visible = false;
      this.toasts$.next([...currentToasts]);
      
      // Remove from array after animation completes
      setTimeout(() => {
        this.toasts$.next(currentToasts.filter(t => t.id !== id));
      }, 300);
    }
  }

  dismissAll(): void {
    const currentToasts = this.toasts$.value;
    currentToasts.forEach(toast => toast.visible = false);
    this.toasts$.next([...currentToasts]);
    
    setTimeout(() => {
      this.toasts$.next([]);
    }, 300);
  }

  /**
   * Configura todos los valores por defecto de una vez
   * Esta es la forma recomendada de configurar los toasts a nivel global de aplicación
   * @param config - Objeto con las configuraciones globales
   * @example
   * // En AppComponent
   * ngOnInit() {
   *   this.toastService.setGlobalConfig({
   *     position: 'top-right',
   *     animation: 'fadeInSlide',
   *     duration: 5000,
   *     offset: { top: 64 }
   *   });
   * }
   */
  setGlobalConfig(config: ToastGlobalConfig): void {
    if (config.position !== undefined) {
      this.defaultPosition = config.position;
    }
    if (config.animation !== undefined) {
      this.defaultAnimation = config.animation;
    }
    if (config.duration !== undefined) {
      this.defaultDuration = config.duration;
    }
    if (config.offset !== undefined) {
      this.offset$.next(config.offset);
    }
  }

  setDefaultPosition(position: ToastPosition): void {
    this.defaultPosition = position;
  }

  setDefaultAnimation(animation: ToastAnimation): void {
    this.defaultAnimation = animation;
  }

  setDefaultDuration(duration: number): void {
    this.defaultDuration = duration;
  }

  /**
   * Configura el offset (espacio) para los toasts
   * Útil para posicionar los toasts debajo del navbar u otros elementos
   * @param offset - Objeto con valores en píxeles para top, bottom, left, right
   * @example
   * toastService.setOffset({ top: 64 }); // 64px desde arriba
   * toastService.setOffset({ top: 64, right: 16 }); // 64px arriba, 16px derecha
   */
  setOffset(offset: ToastOffset): void {
    this.offset$.next(offset);
  }

  /**
   * Reinicia el offset a los valores predeterminados (sin offset)
   */
  resetOffset(): void {
    this.offset$.next({});
  }

  private generateId(): string {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}
